import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Empty,
  Flex,
  Input,
  AutoComplete,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import FeatureNewsCard from "./FeatureNewsCard";
import FeatureNewsEditor from "./FeatureNewsEditor";
import {
  DEFAULT_FEATURE_NEWS_DESIGN,
  FEATURE_NEWS_DESIGNS,
  FEATURE_NEWS_KICKERS,
} from "./featureNewsDesigns";
import {
  createFeatureNews,
  deleteFeatureNews,
  getFeatureNews,
  listAdminFeatureNews,
  updateFeatureNews,
} from "./featureNewsApi";
import { newsBodyHasText } from "./sanitizeNewsHtml";
import { useFeatureNewsPanelSize } from "./featureNewsLayout";
import { getApiErrorMessage } from "../../utils/apiError";
import "./featureNews.css";

const emptyForm = () => ({
  title: "",
  bodyHtml: "",
  designId: DEFAULT_FEATURE_NEWS_DESIGN,
  kicker: "New Feature",
  isActive: true,
});

const FeatureNewsStatusToggle = ({ checked, loading, onChange }) => (
  <div className={`feature-news-status-toggle${checked ? " is-active" : " is-inactive"}`}>
    <button
      type="button"
      className={`feature-news-status-option${checked ? " is-selected" : ""}`}
      disabled={loading}
      onClick={() => {
        if (!checked) {
          onChange(true);
        }
      }}
    >
      Active
    </button>
    <button
      type="button"
      className={`feature-news-status-option${!checked ? " is-selected" : ""}`}
      disabled={loading}
      onClick={() => {
        if (checked) {
          onChange(false);
        }
      }}
    >
      Inactive
    </button>
  </div>
);

const FeatureNewsAdminModal = ({ open, onClose, token }) => {
  const [mode, setMode] = useState("list");
  const { adminWidth } = useFeatureNewsPanelSize(mode);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [togglingId, setTogglingId] = useState(null);

  const loadItems = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    try {
      const data = await listAdminFeatureNews(token);
      setItems(data);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load feature news."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      setMode("list");
      setEditingId(null);
      setForm(emptyForm());
      loadItems();
    }
  }, [open, loadItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setMode("form");
  };

  const openEdit = async (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      bodyHtml: "",
      designId: item.designId || DEFAULT_FEATURE_NEWS_DESIGN,
      kicker: item.kicker || "New Feature",
      isActive: item.isActive !== false,
    });
    setMode("form");
    try {
      const news = await getFeatureNews(token, item.id);
      setForm({
        title: news?.title || item.title || "",
        bodyHtml: news?.bodyHtml || "",
        designId: news?.designId || item.designId || DEFAULT_FEATURE_NEWS_DESIGN,
        kicker: news?.kicker || item.kicker || "New Feature",
        isActive: news?.isActive !== false,
      });
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load feature news."));
      setMode("list");
      setEditingId(null);
    }
  };

  const handleToggleActive = async (item, isActive) => {
    setTogglingId(item.id);
    try {
      await updateFeatureNews(token, item.id, { isActive });
      setItems((prev) =>
        prev.map((news) => (news.id === item.id ? { ...news, isActive } : news))
      );
      message.success(isActive ? "Feature news is visible to users." : "Feature news is hidden from users.");
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to update feature news."));
    } finally {
      setTogglingId(null);
    }
  };

  const handleSave = async () => {
    const title = form.title.trim();
    if (!title) {
      message.warning("Add a title.");
      return;
    }
    if (!newsBodyHasText(form.bodyHtml)) {
      message.warning("Add news body content.");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateFeatureNews(token, editingId, form);
        message.success("Feature news updated.");
      } else {
        await createFeatureNews(token, form);
        message.success("Feature news created.");
      }
      setMode("list");
      await loadItems();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to save feature news."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFeatureNews(token, id);
      message.success("Feature news deleted.");
      await loadItems();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to delete feature news."));
    }
  };

  return (
    <Modal
      title={mode === "form" ? (editingId ? "Edit feature news" : "New feature news") : "Feature news"}
      open={open}
      onCancel={mode === "form" ? () => setMode("list") : onClose}
      width={adminWidth}
      className="feature-news-admin-modal"
      rootClassName="feature-news-admin-modal-root"
      destroyOnHidden
      footer={
        mode === "form" ? (
          <Space>
            <Button onClick={() => setMode("list")}>Back</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {mode === "list" ? (
        <>
          <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary">
              Create announcements users see once after they sign in.
            </Typography.Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add news
            </Button>
          </Flex>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <Empty description="No feature news yet" />
          ) : (
            items.map((item) => (
              <div key={item.id} className="feature-news-admin-item">
                <div
                  className={`feature-news-design-swatch feature-news-design-swatch--${item.designId}`}
                  style={{ width: 52, height: 52, flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text strong ellipsis>
                    {item.title}
                  </Typography.Text>
                  <div>
                    <Typography.Text type="secondary">
                      {item.isActive ? "Active · visible to users" : "Inactive · hidden from users"}
                      {item.kicker ? ` · ${item.kicker}` : ""}
                      {item.designId ? ` · ${item.designId}` : ""}
                    </Typography.Text>
                  </div>
                </div>
                <Space>
                  <FeatureNewsStatusToggle
                    checked={item.isActive !== false}
                    loading={togglingId === item.id}
                    onChange={(checked) => handleToggleActive(item, checked)}
                  />
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(item)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this news?"
                    okText="Delete"
                    onConfirm={() => handleDelete(item.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              </div>
            ))
          )}
        </>
      ) : (
        <Flex gap={24} align="flex-start" wrap="wrap">
          <div style={{ flex: "1 1 360px", minWidth: 280 }}>
            <Typography.Text strong>Title</Typography.Text>
            <Input
              value={form.title}
              maxLength={120}
              placeholder="Short headline"
              style={{ margin: "8px 0 16px" }}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
            />
            <Typography.Text strong>Badge label</Typography.Text>
            <AutoComplete
              style={{ width: "100%", margin: "8px 0 16px" }}
              options={FEATURE_NEWS_KICKERS.map((label) => ({ value: label }))}
              value={form.kicker}
              maxLength={40}
              placeholder="New Feature"
              onChange={(kicker) => setForm((prev) => ({ ...prev, kicker }))}
              filterOption={(input, option) =>
                (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
            <Typography.Text strong>Design</Typography.Text>
            <div className="feature-news-design-grid" style={{ margin: "8px 0 16px" }}>
              {FEATURE_NEWS_DESIGNS.map((design) => (
                <button
                  key={design.id}
                  type="button"
                  className={`feature-news-design-option${form.designId === design.id ? " is-selected" : ""}`}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      designId: design.id,
                      kicker: FEATURE_NEWS_KICKERS.includes(prev.kicker) || !prev.kicker
                        ? design.kicker
                        : prev.kicker,
                    }))
                  }
                >
                  <div className={`feature-news-design-swatch feature-news-design-swatch--${design.id}`} />
                  <Typography.Text strong>{design.name}</Typography.Text>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {design.description}
                    </Typography.Text>
                  </div>
                </button>
              ))}
            </div>
            <Flex align="center" justify="space-between" style={{ marginBottom: 12 }}>
              <Typography.Text strong>Status</Typography.Text>
              <FeatureNewsStatusToggle
                checked={form.isActive}
                onChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
              />
            </Flex>
            <Typography.Text strong>Body</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <FeatureNewsEditor
                value={form.bodyHtml}
                onChange={(bodyHtml) => setForm((prev) => ({ ...prev, bodyHtml }))}
              />
            </div>
          </div>
          <div style={{ flex: "1 1 280px", minWidth: 240 }}>
            <Typography.Text type="secondary">Preview</Typography.Text>
            <div className={`feature-news-dock feature-news-dock--${form.designId}`} style={{ marginTop: 8, width: "100%" }}>
              <FeatureNewsCard
                item={{
                  title: form.title || "Headline",
                  bodyHtml: form.bodyHtml || "<p>Your announcement will appear here.</p>",
                  designId: form.designId,
                  kicker: form.kicker,
                }}
              />
              <div className="feature-news-dock-footer">
                <p className="feature-news-faq-hint">
                  <QuestionCircleOutlined />
                  <span>More detail in the FAQ, top right</span>
                </p>
                <div className="feature-news-actions">
                  <Button type="primary">Got it</Button>
                </div>
              </div>
            </div>
          </div>
        </Flex>
      )}
    </Modal>
  );
};

export default FeatureNewsAdminModal;
